/* eslint-disable react/function-component-definition */
import React, {
  useCallback,
  useMemo,
  useState,
  useContext,
  useEffect,
} from 'react';
import { Select } from 'antd';
import { FormOutlined, SettingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

import BorderlessButton from '../../common/buttons/BorderlessButton';

import WorkflowActionNode from './WorkflowActionNode';
import FormSelector from './selectors/FormSelector';
import UserAssignmentSelector from './selectors/UserAssignmentSelector';
import WorkflowHandle from './WorkflowHandle';
import WorkflowTriggerConfigureDrawer from './WorkflowTriggerConfigureDrawer';

import {
  locateWorkflowParentElement,
  updateData,
} from './workflowHelpers';
import { createSourceToTargetMap, createTargetToSourceMap, resolveFormDivisionIds } from '../formHelpers';
import { AdditionalItem } from './AssignmentDropdownItems';
import FormWorkflowContext from './FormWorkflowContext';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import CobraPMASelector from './selectors/CobraPMASelector';
import useToggle from '../../common/hooks/useToggle';
import WorkflowConfigureSourceSelector from './selectors/WorkflowConfigureSourceSelector';

const HOVER_TEXT = `
Use this step to generate Preventative Maintenance Forms based on the Patrol and Label selected.
`;

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the <b>Select Form & Select Patrol & Select Label & Select Users</b>{' '}
      fields to be configured.
    </div>
  ),
};

const AUTHOR_HELP = 'The new form will be assigned to the original form\'s author';

export default function WorkflowCustomCobraPMANode({
  setDataMap,
  setElements,
  isDisplay,
  divisionId,
  divisionIds = [],
  templateId,
  name,
  sections = [],
  projectId,
  isExternalForm,
  setDisableNext,
} = {}) {
  const relevantDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });

  return function WorkflowCustomCobraPMANodeInner({
    id,
    draggable,
    data = {},
  }) {
    const {
      elements = [],
      dataMap,
    } = useContext(FormWorkflowContext) ?? {};

    const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
    const buckets = useSelector((state) => state.buckets.buckets);
    const labels = useSelector((state) => state.labels);

    const parentCreateBoardCardNode = useMemo(() => (
      locateWorkflowParentElement(id, 'createBoardCard', elements)
    ), [id, elements, dataMap]);

    const {
      form: initialForm,
      users: initialUsers,
      fieldMappings: initialFieldMappings = {},
      sharedForm: initialSharedForm,
      linkedNumber: initialLinkedNumber,
      patrol: initialPatrol,
      labelFilter: initialLabel,
    } = data;

    const { toggle, isToggled } = useToggle();

    const [selectedForm, setSelectedForm] = useState(initialForm);
    const [selectedUsers, setSelectedUsers] = useState(initialUsers || []);
    const [fieldMappings, setFieldMappings] = useState(
      initialFieldMappings?.version === 'v2' ? initialFieldMappings : createTargetToSourceMap(initialFieldMappings)
    );
    const [isSharedForm, setIsSharedForm] = useState(initialSharedForm);
    const [linkedNumber, setLinkedNumber] = useState(initialLinkedNumber);
    const [selectedPatrol, setSelectedPatrol] = useState(initialPatrol);
    const [selectedLabel, setSelectedLabel] = useState(initialLabel);

    useEffect(() => {
      if (!setDisableNext) return;

      const disableNext = !selectedForm
        || !selectedPatrol
        || !selectedLabel
        || !selectedUsers?.length;
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [
      selectedForm,
      selectedPatrol,
      selectedLabel,
      selectedUsers,
      setDisableNext,
      id,
    ]);

    const onFormChange = useCallback((form) => {
      if (!setDataMap || !id) return;
      setSelectedForm(form);
      setDataMap(updateData(id, { form }));
    }, [setDataMap, id]);

    const onAssignmentChange = useCallback((users) => {
      if (!setDataMap || !id) return;
      setSelectedUsers(users);
      setDataMap(updateData(id, { users }));
    }, [setDataMap, id]);

    const onPatrolChange = useCallback((patrol) => {
      if (!setDataMap || !id) return;
      setSelectedPatrol(patrol);
      setDataMap(updateData(id, { patrol }));
    }, [setDataMap, id]);

    const onLabelChange = useCallback((labelFilter) => {
      if (!setDataMap || !id) return;
      setSelectedLabel(labelFilter);
      setDataMap(updateData(id, { labelFilter }));
    }, [setDataMap, id]);

    const onFieldMappingsChange = useCallback((newMappings, formId) => {
      if (!setDataMap || !id) return;
      const updatedFieldMappings = {
        ...fieldMappings,
        version: 'v2',
        [formId]: createSourceToTargetMap(newMappings),
      };

      setFieldMappings(updatedFieldMappings);
      setDataMap(updateData(id, { fieldMappings: updatedFieldMappings }));
    }, [setDataMap, id, fieldMappings]);

    const onCheckChanged = useCallback((key) => (checked) => {
      if (key === 'sharedForm') setIsSharedForm(checked);
      if (key === 'linkedNumber') setLinkedNumber(checked);
      setDataMap(updateData(id, { [key]: checked }));
    }, [setDataMap, id]);

    const additionalFormOptions = useMemo(() => {
      const formDropdownFields = [];

      sections.forEach(({ name: sectionName, fields = [] }) => {
        fields?.forEach(({ selectedType, configProps, id: fieldId }) => {
          if (selectedType === 'dropdown' && configProps?.dataType === 'Forms') {
            formDropdownFields.push({
              sectionName,
              fieldName: configProps?.title,
              fieldId,
            });
          }
        });
      });

      return formDropdownFields.map(({ sectionName, fieldName, fieldId }) => (
        <Select.Option key={fieldId} value={fieldId} label={`${sectionName} - ${fieldName}`}>
          <AdditionalItem
            title={`${sectionName} - ${fieldName}`}
            help="Selected forms from this field will be initiated"
          />
        </Select.Option>
      ));
    }, [sections]);

    const relevantPatrolBucketTemplateId = useMemo(() => (
      bucketTemplates.find(({ name: bucketTemplateName }) => bucketTemplateName?.includes('Patrol'))?.id
    ), [bucketTemplates]);

    const relevantPatrolBuckets = useMemo(() => (
      buckets.filter(({ bucketTemplateId }) => bucketTemplateId === relevantPatrolBucketTemplateId)
    ), [buckets, relevantPatrolBucketTemplateId]);

    const patrolOptions = useMemo(() => {
      const options = [];

      relevantPatrolBuckets.forEach(({ id: bucketId, name: bucketName }) => {
        options.push({
          value: bucketId,
          label: bucketName,
        });
      });

      sections.forEach(({ fields: sectionFields, name: sectionName }) => {
        sectionFields?.forEach(({ selectedType, configProps, id: fieldId }) => {
          if (
            selectedType === 'dropdown'
            && configProps?.dataType === 'Buckets'
            && configProps?.subDataType === relevantPatrolBucketTemplateId
          ) {
            options.push({
              value: fieldId,
              label: `${sectionName}: ${configProps.title}`,
            });
          }
        });
      });

      return options;
    }, [relevantPatrolBuckets, relevantPatrolBucketTemplateId, sections]);

    const equipmentLabelOptions = useMemo(() => {
      const options = [];
      labels.forEach((label) => {
        if (label.type === 'equipment') {
          options.push({
            value: label.id,
            label: label.title,
          });
        }
      });

      sections.forEach(({ fields: sectionFields = [], name: sectionName }) => {
        sectionFields?.forEach(({ selectedType, configProps, id: fieldId }) => {
          if (selectedType === 'dropdown' && configProps?.dataType === 'Labels' && configProps?.subDataType?.includes('equipment')) {
            options.push({
              value: fieldId,
              label: `${sectionName}: ${configProps.title}`,
            });
          }
        });
      });

      return options;
    }, [labels, sections]);

    const fieldRenderMap = {
      text: ({
        fieldMappings,
        sourceFieldMap,
        defaultValue,
        onFieldMappingsChange,
        targetId,
        formId,
        value,
      }) => {
        const {
          text = [],
        } = sourceFieldMap;
        const eligibleSourceFields = [...text];
        eligibleSourceFields.unshift({
          id: `createdFormNumber`,
          title: 'Created Form - Number',
          helpText: 'If set, it will be auto-filled with the number of the form that was created',
        });

        return (
          <WorkflowConfigureSourceSelector
            fieldMappings={fieldMappings}
            defaultValue={defaultValue}
            onFieldMappingsChange={onFieldMappingsChange}
            targetId={targetId}
            formId={formId}
            value={value}
            eligibleSourceFields={eligibleSourceFields}
          />
        );
      },
      'dropdown - Cards': ({
        fieldMappings,
        sourceFieldMap,
        defaultValue,
        onFieldMappingsChange,
        targetId,
        formId,
        value,
      }) => {
        const {
          'dropdown - Cards': cards = [],
        } = sourceFieldMap;
        const eligibleSourceFields = [...cards];
        if (parentCreateBoardCardNode) {
          eligibleSourceFields.unshift({
            id: `createdBoardCard.${parentCreateBoardCardNode.id}`,
            title: 'Created Card',
            helpText: 'This field will only be available if the form is triggered from a created card',
          });
        }

        return (
          <WorkflowConfigureSourceSelector
            fieldMappings={fieldMappings}
            defaultValue={defaultValue}
            onFieldMappingsChange={onFieldMappingsChange}
            targetId={targetId}
            formId={formId}
            value={value}
            eligibleSourceFields={eligibleSourceFields}
          />
        );
      },
      'dropdown - Projects': ({
        fieldMappings,
        sourceFieldMap,
        defaultValue,
        onFieldMappingsChange,
        targetId,
        formId,
        value,
      }) => {
        const {
          'dropdown - Projects': projects = [],
        } = sourceFieldMap;
        const eligibleSourceFields = [...projects];
        eligibleSourceFields.unshift({
          id: 'relevantLoopedLocation',
          title: 'Filtered Location',
          helpText: 'If set, dropdown will be filled with the location of a location inside the selected patrol with equipment that matches the label filter.',
        });

        return (
          <WorkflowConfigureSourceSelector
            fieldMappings={fieldMappings}
            defaultValue={defaultValue}
            onFieldMappingsChange={onFieldMappingsChange}
            targetId={targetId}
            formId={formId}
            value={value}
            eligibleSourceFields={eligibleSourceFields}
          />
        );
      },
    };

    return (
      <WorkflowActionNode
        title="Preventative Maintenance Generation"
        Icon={FormOutlined}
        type="customCobraPMA"
        id={id}
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay}
        hover={HOVER_TEXT}
        errorPopover={
          !selectedForm || !selectedPatrol || !selectedLabel || !selectedUsers?.length
            ? ERROR_DETAILS
            : null
        }
      >
        {!draggable && (
          <div>
            <CobraPMASelector
              type="Patrol"
              options={patrolOptions}
              onChange={onPatrolChange}
              selected={selectedPatrol}
              disable={draggable || isDisplay}
            />
            <CobraPMASelector
              type="Label"
              options={equipmentLabelOptions}
              onChange={onLabelChange}
              selected={selectedLabel}
              disable={draggable || isDisplay}
            />
            <OnTraccrCheckbox
              style={{ marginTop: 5 }}
              onChange={onCheckChanged('sharedForm')}
              defaultChecked={initialSharedForm}
              value={isSharedForm}
              label="Shared Form?"
              hoverNode={(
                <div style={{ width: 300 }}>
                  Check this box if you want to create one form and assign it to multiple people <br/> <br/>
                  Leave this box unchecked if you want to create a seperate form for each person.
                </div>
              )}
            />
            <OnTraccrCheckbox
              style={{ marginTop: 5 }}
              onChange={onCheckChanged('linkedNumber')}
              defaultChecked={initialLinkedNumber}
              value={linkedNumber}
              label="Link Number?"
              hoverNode={(
                <div style={{ width: 300 }}>
                  Check this box if you want the triggered form's number to be linked to this form's number <br/> <br/>
                  Leave this box unchecked if you want the triggered form to have its own number.
                </div>
              )}
            />
            <FormSelector
              divisionId={divisionId}
              disable={draggable || isDisplay}
              onChange={onFormChange}
              selected={selectedForm}
              projectId={projectId}
              hideExternalForms
              additionalFormOptions={additionalFormOptions}
              templateId={templateId}
            />
            <UserAssignmentSelector
              type="user"
              isDisplay={isDisplay}
              draggable={draggable}
              divisions={relevantDivisionIds}
              showPositions
              onChange={onAssignmentChange}
              selected={selectedUsers}
              showFields
              sections={sections}
              showFormAuthor={!isExternalForm}
              formAuthorHelperText={AUTHOR_HELP}
            />
            <BorderlessButton
              title="Configure"
              style={{ margin: 5 }}
              iconNode={<SettingOutlined />}
              onClick={toggle}
              disabled={!selectedForm}
            />
            <WorkflowTriggerConfigureDrawer
              visible={isToggled}
              onClose={toggle}
              onSubmit={toggle}
              targetFormId={selectedForm}
              sourceName={name}
              sourceSections={sections}
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onFieldMappingsChange}
              divisionId={divisionId}
              templateId={templateId}
              parentCreateBoardCardNode={parentCreateBoardCardNode}
              canMapShiftToTime
              fieldRenderMap={fieldRenderMap}
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay}/>
          </div>
        )}
      </WorkflowActionNode>
    );
  };
}
